import React, { useState, useEffect } from 'react'
import { makeStyles, Grid, Button } from '@material-ui/core'
import { getFromApi } from 'actions'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import { useStateValue } from 'context/store'
import CheckboxesGroup from './components/CheckboxesGroup'
import CheckboxesLists from './components/CheckboxesLists'
import RadioButtonsGroup from './components/RadioButtonsGroup'
import ListContainer from './components/ListContainer'
import {
   getAllItems,
   hideItemsLists,
   searchItemsByParentItemId,
   onItemSelection,
   setItemsByParentItemId,
   shouldShowItemList
} from './actions'
import {
   getDefaultSelectedOptions,
   convertArrayToMap,
   setDefaultSelectedOptions,
   getSelectedOptions,
   deselectItems,
   getItemName
} from './utils'
import { useHasOperation } from 'hooks/useHasOperation'
import { OPERACIONES_ROLES } from 'utils/constants'

const useStyles = makeStyles(theme => ({
   formContainer: {
      width: '100%',
      minHeight: 350
   },
   button: {
      width: 150,
      marginLeft: theme.spacing(2.5),
      height: 40,
      float: 'right'
   },
   buttonsContainer: {
      width: '100%',
      height: 40,
      marginTop: theme.spacing(1)
   }
}))

const LOADINGS_INITIAL_STATE = {
   roles: false,
   applications: false,
   modules: false,
   functionalities: false,
   operations: false
}

const CHECKS_INITIAL_STATE = {
   roles: false,
   applications: false,
   modules: false,
   functionalities: false,
   operations: false
}

// ----------------- COMPONENT ---------------------
const LinkRoleToOperationForm = ({
   selectedOpts = [],
   linkRoleToOperation
}) => {
   const classes = useStyles()

   const [loading, setLoading] = useState(LOADINGS_INITIAL_STATE)
   const [check, setCheck] = useState(CHECKS_INITIAL_STATE)

   const [rolesList, setRolesList] = useState([])
   const [applicationsList, setApplicationsList] = useState({})
   const [modulesLists, setModulesLists] = useState({})
   const [functionalitiesLists, setFunctionalitiesLists] = useState({})
   const [operationsLists, setOperationsLists] = useState({})

   const [showModulesList, setShowModulesList] = useState(false)
   const [showFunctionalitiesList, setShowFunctionalitiesList] = useState(false)
   const [showOperationsList, setShowOperationsLists] = useState(false)

   const [_, dispatch] = useStateValue()
   const RELACIONAR_ROL_A_OPERACION = useHasOperation(OPERACIONES_ROLES.RELACIONAR_ROL_A_OPERACION)

   const [selectedOptions, setSelectedOptions, selectedOptionsInitialState] =
      selectedOpts

   function clean() {
      setSelectedOptions(selectedOptionsInitialState)
      setApplicationsList(previous => deselectItems(previous))
      setModulesLists({})
      setFunctionalitiesLists({})
      setOperationsLists({})
      setShowOperationsLists(false)
      setShowModulesList(false)
      setShowFunctionalitiesList(false)
      setCheck(CHECKS_INITIAL_STATE)
      setLoading(LOADINGS_INITIAL_STATE)
   }

   useEffect(() => {
      clean()
      getAllActiveRolesAndApplications()
   }, []) // eslint-disable-line react-hooks/exhaustive-deps

   function setLoadingsState(newState) {
      setLoading(previous => ({
         ...previous,
         ...newState
      }))
   }

   function setChecksState(newState) {
      setCheck(previous => ({
         ...previous,
         ...newState
      }))
   }

   // ----------- GET INITIAL ITEMS/LISTS ----------------
   async function _getInitialModules(optionsMap) {
      await getAllItems(
         `${process.env.REACT_APP_MODULES_API}`,
         optionsMap,
         'application',
         'applications',
         'modules',
         setModulesLists
      )
   }
   const getInitialModules = useApiRequest(
      _getInitialModules,
      'Rol a Operación: Consultar Módulos Iniciales',
      false
   )

   async function _getInitialFunctionalities(optionsMap) {
      await getAllItems(
         `${process.env.REACT_APP_FUNCTIONALITIES_API}`,
         optionsMap,
         'module',
         'modules',
         'functionalities',
         setFunctionalitiesLists
      )
   }
   const getInitialFunctionalities = useApiRequest(
      _getInitialFunctionalities,
      'Rol a Operación: Consultar funcionalidades Iniciales',
      false
   )

   async function _getInitialOperations(optionsMap) {
      await getAllItems(
         `${process.env.REACT_APP_OPERATIONS_API}`,
         optionsMap,
         'functionality',
         'functionalities',
         'operations',
         setOperationsLists
      )
   }
   const getInitialOperations = useApiRequest(
      _getInitialOperations,
      'Rol a Operación: Consultar Operaciones Iniciales',
      false
   )

   async function _getLinkedOperations(roleId) {
      setLoadingsState({ applications: true })
      const url = `${process.env.REACT_APP_ROLES_API}/roles/operations`
      const payload = { roleId }
      const linkedOperations = await getFromApi(url, payload)
      if (linkedOperations && linkedOperations.length) {
         const optionsMap = getDefaultSelectedOptions(linkedOperations)
         const appsWithSelection = setDefaultSelectedOptions(
            applicationsList,
            optionsMap.applications
         )
         setApplicationsList(appsWithSelection)
         await Promise.all([
            getInitialModules(optionsMap),
            getInitialFunctionalities(optionsMap),
            getInitialOperations(optionsMap)
         ])
      } else {
         const message = 'No se encontraron Operaciones relacionadas al Rol'
         dispatch({
            type: 'OPEN_SNACKBAR',
            openMessage: {
               message: `${message}: ${getItemName(rolesList, roleId)}`,
               severity: 'info'
            }
         })
      }
      setLoadingsState({ applications: false })
   }
   const getLinkedOperations = useApiRequest(
      _getLinkedOperations,
      'Rol a Operación: Consultar Relaciones Iniciales'
   )

   // ----------------------- ROLES AND APPS -------------------------------

   async function _getAllActiveRolesAndApplications() {
      setChecksState({ applications: false, roles: false })
      setLoadingsState({ applications: true, roles: true })
      const [activeRoles, activeApplications] = await Promise.all([
         getFromApi(`${process.env.REACT_APP_ROLES_API}/roles/active`),
         getFromApi(
            `${process.env.REACT_APP_APPLICATIONS_API}/applications/active`
         )
      ])
      if (activeRoles) {
         const mappedRoles = activeRoles.map(role => ({
            id: role.id.toString(),
            name: role.name
         }))
         setRolesList(mappedRoles)
      }
      if (activeApplications) {
         const applicationsMap = convertArrayToMap(activeApplications)
         setApplicationsList(applicationsMap)
      }
      setLoadingsState({ applications: false, roles: false })
   }
   const getAllActiveRolesAndApplications = useApiRequest(
      _getAllActiveRolesAndApplications,
      'Rol a Operación: Consultar Roles y Aplicaciones'
   )

   function onRoleSelection(event) {
      setApplicationsList(previous => deselectItems(previous))
      hideItemsLists(operationsLists, setOperationsLists)
      hideItemsLists(functionalitiesLists, setFunctionalitiesLists)
      hideItemsLists(modulesLists, setModulesLists)
      const { value: roleId } = event.target
      setSelectedOptions(previous => ({
         ...previous,
         role: {
            id: roleId,
            name: getItemName(rolesList, roleId)
         }
      }))
      setChecksState({ roles: true })
      getLinkedOperations(roleId)
   }

   useEffect(() => {
      setSelectedOptions(previous => ({
         ...previous,
         applications: getSelectedOptions(applicationsList)
      }))
   }, [applicationsList])

   useEffect(() => {
      setChecksState({ applications: selectedOptions?.applications.length > 0 })
   }, [selectedOptions?.applications])

   function onApplicationSelection(event) {
      const { name: applicationId, checked } = event.target
      setApplicationsList(previous => ({
         ...previous,
         [applicationId]: {
            ...previous[applicationId],
            checked
         }
      }))
      setModulesByApplicationId(applicationId, checked)
   }

   // ---------------------- MODULES --------------------------
   useEffect(() => {
      shouldShowItemList(
         modulesLists,
         setShowModulesList,
         setSelectedOptions,
         'modules'
      )
   }, [modulesLists])

   useEffect(() => {
      setChecksState({ modules: selectedOptions?.modules.length > 0 })
   }, [selectedOptions?.modules])

   async function _searchModulesByApplicationId(applicationId) {
      setLoadingsState({ modules: true })
      await searchItemsByParentItemId(
         `${process.env.REACT_APP_MODULES_API}/modules/search`,
         'applicationId',
         applicationId,
         'application',
         setModulesLists,
         'No se encontraron Módulos para la Applicación seleccionada',
         dispatch
      )
      setLoadingsState({ modules: false })
   }
   const searchModulesByApplicationId = useApiRequest(
      _searchModulesByApplicationId,
      'Rol a Operación: Consultar Módulos por Aplicación',
      false
   )

   function setModulesByApplicationId(applicationId, checkedApplication) {
      setItemsByParentItemId(
         applicationId,
         checkedApplication,
         modulesLists,
         setModulesLists,
         searchModulesByApplicationId,
         functionalitiesLists,
         setFunctionalitiesByModuleId
      )
   }

   function onModuleSelection(event) {
      onItemSelection(event, setModulesLists, setFunctionalitiesByModuleId)
   }

   // ------------------ FUNCTIONALITIES ------------------------

   useEffect(() => {
      shouldShowItemList(
         functionalitiesLists,
         setShowFunctionalitiesList,
         setSelectedOptions,
         'functionalities'
      )
   }, [functionalitiesLists])

   useEffect(() => {
      setChecksState({
         functionalities: selectedOptions?.functionalities.length > 0
      })
   }, [selectedOptions?.functionalities])

   async function _searchFunctionalitiesByModuleId(moduleId) {
      setLoadingsState({ functionalities: true })
      await searchItemsByParentItemId(
         `${process.env.REACT_APP_FUNCTIONALITIES_API}/functionalities/search`,
         'moduleId',
         moduleId,
         'module',
         setFunctionalitiesLists,
         'No se encontraron Funcionalidades para el Módulo seleccionado',
         dispatch
      )
      setLoadingsState({ functionalities: false })
   }
   const searchFunctionalitiesByModuleId = useApiRequest(
      _searchFunctionalitiesByModuleId,
      'Rol a Operación: Consultar Funcionalidad por Módulo',
      false
   )

   function setFunctionalitiesByModuleId(moduleId, checkedModule) {
      setItemsByParentItemId(
         moduleId,
         checkedModule,
         functionalitiesLists,
         setFunctionalitiesLists,
         searchFunctionalitiesByModuleId,
         operationsLists,
         setOperationsByFunctionalityId
      )
   }

   function onFunctionalitySelection(event) {
      onItemSelection(
         event,
         setFunctionalitiesLists,
         setOperationsByFunctionalityId
      )
   }

   // --------------------  OPERATIONS ----------------------------

   useEffect(() => {
      shouldShowItemList(
         operationsLists,
         setShowOperationsLists,
         setSelectedOptions,
         'operations'
      )
   }, [operationsLists])

   useEffect(() => {
      setChecksState({ operations: selectedOptions?.operations.length > 0 })
   }, [selectedOptions?.operations])

   async function _searchOperationsByFunctionalityId(functionalityId) {
      setLoadingsState({ operations: true })
      await searchItemsByParentItemId(
         `${process.env.REACT_APP_OPERATIONS_API}/operations/search`,
         'functionalityId',
         functionalityId,
         'functionality',
         setOperationsLists,
         'No se encontraron Operaciones para la Funcionalidad seleccionada',
         dispatch
      )
      setLoadingsState({ operations: false })
   }
   const searchOperationsByFunctionalityId = useApiRequest(
      _searchOperationsByFunctionalityId,
      'Rol a Operación: Consultar Operaciones por Funcionalidad',
      false
   )

   function setOperationsByFunctionalityId(
      functionalityId,
      checkedFunctionality
   ) {
      setItemsByParentItemId(
         functionalityId,
         checkedFunctionality,
         operationsLists,
         setOperationsLists,
         searchOperationsByFunctionalityId
      )
   }

   function onOperationSelection(event) {
      onItemSelection(event, setOperationsLists)
   }

   // ---------------- JSX ---------------
   return (
      <Grid className={classes.formContainer}>
         <Grid>
            {rolesList && !!rolesList.length && (
               <ListContainer
                  title={'Roles'}
                  loading={loading.roles}
                  check={check.roles}
               >
                  <RadioButtonsGroup
                     name={'Roles'}
                     items={rolesList}
                     value={selectedOptions.role.id}
                     handleSelection={onRoleSelection}
                  />
               </ListContainer>
            )}
            {!!Object.keys(applicationsList).length && (
               <ListContainer
                  title={'Aplicaciones'}
                  loading={loading.applications}
                  check={check.applications}
               >
                  <CheckboxesGroup
                     items={applicationsList}
                     handleSelection={onApplicationSelection}
                  />
               </ListContainer>
            )}
            {showModulesList && (
               <ListContainer
                  title={'Módulos'}
                  loading={loading.modules}
                  check={check.modules}
               >
                  <CheckboxesLists
                     optionsLists={modulesLists}
                     handleSelection={onModuleSelection}
                  />
               </ListContainer>
            )}
            {showFunctionalitiesList && (
               <ListContainer
                  title={'Funcionalidades'}
                  loading={loading.functionalities}
                  check={check.functionalities}
               >
                  <CheckboxesLists
                     optionsLists={functionalitiesLists}
                     handleSelection={onFunctionalitySelection}
                  />
               </ListContainer>
            )}
            {showOperationsList && (
               <ListContainer
                  title={'Operaciones'}
                  loading={loading.operations}
                  check={check.operations}
               >
                  <CheckboxesLists
                     optionsLists={operationsLists}
                     handleSelection={onOperationSelection}
                  />
               </ListContainer>
            )}
         </Grid>
         {rolesList && !!rolesList.length && (
            <Grid className={classes.buttonsContainer}>
               <Button
                  onClick={linkRoleToOperation}
                  variant="contained"
                  className={classes.button}
                  color="primary"
                  disabled={
                     !selectedOptions.role.id ||
                     !selectedOptions.operations.length || 
                     !RELACIONAR_ROL_A_OPERACION
                  }
               >
                  GUARDAR
               </Button>
               <Button
                  onClick={clean}
                  variant="outlined"
                  className={classes.button}
                  color="primary"
               >
                  LIMPIAR
               </Button>
            </Grid>
         )}
      </Grid>
   )
}

export default LinkRoleToOperationForm
